import React from 'react';

// One-shot guard so a chunk-load loop can't reload the tab endlessly if the
// server genuinely has no matching bundle. Stored in sessionStorage so a
// full refresh of the tab clears it and normal behaviour resumes.
const CHUNK_RELOAD_FLAG = 'e1_chunk_reload_attempted';

function isChunkLoadError(err) {
  if (!err) return false;
  const msg = String(err.message || err.toString() || '');
  // Matches webpack's `ChunkLoadError`, `Loading chunk ... failed`, and
  // dynamic-import failures. All three surface when the browser holds a
  // stale bundle manifest and requests a chunk hash the current build no
  // longer emits — usually after a deploy or dev-server rebuild.
  return (
    err.name === 'ChunkLoadError' ||
    /Loading chunk .* failed/i.test(msg) ||
    /Loading CSS chunk .* failed/i.test(msg) ||
    /Failed to fetch dynamically imported module/i.test(msg)
  );
}

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null, chunkReloadFailed: false };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('ErrorBoundary caught:', error, errorInfo);
    this.setState({ errorInfo });

    // Self-heal stale-bundle errors: auto-reload once. If we've already
    // reloaded and STILL see the chunk error, the bundle is genuinely
    // broken — surface a clear "Reload" button instead of looping.
    if (isChunkLoadError(error)) {
      const alreadyTried = sessionStorage.getItem(CHUNK_RELOAD_FLAG);
      if (!alreadyTried) {
        sessionStorage.setItem(CHUNK_RELOAD_FLAG, '1');
        // Cache-bust so the browser fetches a fresh index.html + manifest.
        const url = new URL(window.location.href);
        url.searchParams.set('_r', Date.now().toString());
        window.location.replace(url.toString());
        return;
      }
      // Second failure — give up and surface a manual reload UI.
      this.setState({ chunkReloadFailed: true });
    }
  }

  handleReload = () => {
    sessionStorage.removeItem(CHUNK_RELOAD_FLAG);
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      const chunkErr = isChunkLoadError(this.state.error);
      return (
        <div className="p-6 text-center" data-testid="error-boundary-fallback">
          <p className="text-red-600 font-medium mb-2">
            {chunkErr
              ? "This page has been updated. Please reload to get the latest version."
              : "Something went wrong loading this section."}
          </p>
          {!chunkErr && (
            <p className="text-xs text-slate-500 mb-3 max-w-lg mx-auto font-mono break-all">
              {this.state.error?.message || 'Unknown error'}
            </p>
          )}
          {chunkErr ? (
            <button
              onClick={this.handleReload}
              className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm hover:bg-blue-700"
              data-testid="reload-page-btn"
            >
              Reload Page
            </button>
          ) : (
            <button
              onClick={() => this.setState({ hasError: false, error: null, errorInfo: null })}
              className="text-sm text-blue-600 hover:underline"
              data-testid="error-boundary-retry"
            >
              Try again
            </button>
          )}
        </div>
      );
    }
    return this.props.children;
  }
}

export default ErrorBoundary;
